/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
/**
 *
 * OnboardingContainer
 *
 */

import React, { memo, useState, useEffect, useContext } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { createStructuredSelector } from 'reselect';
import { compose } from 'redux';
import {
  Space,
  Conditional,
  Text,
  Button,
  GlobalStyle as CashfreeGlobalStyle,
  TwoFactorAuthModal,
  Image,
  Loader,
} from '@cashfree-intl/coherent';

import { useInjectSaga } from 'utils/injectSaga';
import { useInjectReducer } from 'utils/injectReducer';
import {
  useQuery,
  getReferrerFromURL,
  isYesMerchant,
  isICICIDashboard,
  isIDFCDashboard,
  isLKDashboard,
  isAxisDashboard,
  redirectToCommonAuthLogin,
} from 'utils/common';
import {
  LOGO_MAPPING,
  CF_TNC_URL,
  MERCHANT_DASHBOARD_GPLAY_URL,
  EMAIL_LOGIN,
  MERCHANT_DASHBOARD_APPLE_APP_URL,
  PARTNER,
  CF_SUPPORT_URL,
} from 'utils/constants';

import { getInfoText } from 'utils/modal';
import Analytics from 'utils/analytics';
import EVENTS from 'constants/analytics';

import FormController from 'containers/FormController';
import HeaderScriptLoader from 'components/HeaderScriptLoader';
import CustomIcon from 'components/CustomIcons';

import makeSelectResetPassword from '../ResetPassword/selectors';
import makeSelectOnboardingContainer from './selectors';
import reducer from './reducer';
import saga from './saga';

import {
  enableLoaderAction,
  googleLoginAction,
  resetDataAction,
  toggleOTPModalAction,
  verifyLoginOTPAction,
  storeErrorLoginResponseAction,
  resendLoginOTPAction,
  checkEmailExistsAction,
  setSignupFirstStepDataAction,
  updateFormStatus as updateFormStatusAction,
  disableLoaderAction,
} from './actions';

import {
  SIGNUP,
  LOGIN,
  FORGOT_PASSWORD,
  RESET_PASSWORD,
  USER_DETAILS_COMPLETED,
  GOOGLE_PRIVACY_POLICY,
  GOOGLE_TERMS_OF_SERVICE,
} from './constants';

import SignupBanner from './components/SignupBanner';
import LoginBanner from './components/LoginBanner';
import GoogleLogin from './components/GoogleLogin';
import OnboardingModal from './components/OnboardingModal';
import { basename } from '../../store';

import { AuthContext } from '../../contexts/AuthContext';

export function OnboardingContainer(props) {
  const location = useLocation();
  const navigate = useNavigate();
  const { userObj } = useContext(AuthContext);
  useInjectReducer({ key: 'onboardingContainer', reducer });
  useInjectSaga({ key: 'onboardingContainer', saga });

  const [modalName, resetModalName] = useState('');
  const [accountExistModalData, setAccountExistModalData] = useState({});

  const {
    enableLoader,
    onboardingContainer,
    googleLogin,
    resetPasswordData,
    resetData,
    toggleOTPModal,
    verifyLoginOTP,
    storeErrorLoginResponse,
    resendLoginOTP,
    checkEmailExists,
    setSignupFirstStepData,
    updateFormStatus,
    disableLoader,
  } = props;

  useEffect(() => {
    resetData();
  }, [location.pathname]);

  const {
    isLoading,
    signUpFormStatus,
    loginForm,
    userDetails,
    merchantDetails,
    isFormSubmitted,
    emailLogin,
    aliasLogin,
    otp2faModal,
    active2faMethodData,
    signupFirstStepData,
  } = onboardingContainer;

  const { isLinkExpired } = resetPasswordData;

  const formName = location.pathname
    .split('/')
    .pop()
    .replace('-', '_')
    .toLowerCase();

  const navigateAsPerUserFlow = token => {
    navigate(`${basename}validate/${token}?goto=landing&env=prod`);
  };

  // eslint-disable-next-line react-hooks/rules-of-hooks
  const referrerFromQueryString = useQuery().get('referrer') || '';
  const referrerFromHostName = getReferrerFromURL();
  const referrer = referrerFromQueryString || referrerFromHostName;
  const refCode = useQuery().get('refCode') || '';

  const includeScripts = formName === SIGNUP;
  const onHomePage =
    (formName === LOGIN || signUpFormStatus !== USER_DETAILS_COMPLETED) &&
    formName !== FORGOT_PASSWORD &&
    formName !== RESET_PASSWORD;
  const showSignupFooter = formName === SIGNUP;
  const showRecaptchaText =
    formName === LOGIN ||
    formName === FORGOT_PASSWORD ||
    (formName === RESET_PASSWORD && isLinkExpired);

  const switchForm = e => {
    e.preventDefault();
    if (formName === 'signup') {
      Analytics.trackEvent(EVENTS.ONBOARDING.SWITCH_LOGIN, null, 'OB')();
      redirectToCommonAuthLogin();
    } else {
      Analytics.trackEvent(EVENTS.ONBOARDING.SWITCH_SIGNUP, null, 'OB')();
      navigate(
        `${basename}signup?source-action=Merchant%20login&action=Sign%20Up&button-id=Donthaveanaccount`,
      );
    }
  };

  const closeModal = () => resetModalName('');

  const openTncLink = () => {
    Analytics.trackEvent(EVENTS.SIGNUP.STEP_1.READ_TNC, { referrer }, 'OB')();
    window.open(CF_TNC_URL, '_blank', 'noopener noreferrer');
  };

  const openContactSupport = () => {
    Analytics.trackEvent(EVENTS.SIGNUP.STEP_1.CONTACT_US, { referrer }, 'OB')();
    window.open(CF_SUPPORT_URL, '_blank', 'noopener noreferrer');
  };

  const openPlayStoreAppLink = () => {
    Analytics.trackEvent(EVENTS.GOOGLE_PLAY, null, 'OB')();
    window.open(MERCHANT_DASHBOARD_GPLAY_URL, '_blank', 'noopener noreferrer');
  };

  const openAppleAppLink = () => {
    Analytics.trackEvent(EVENTS.APPLE_APP_STORE, null, 'OB');
    window.open(
      MERCHANT_DASHBOARD_APPLE_APP_URL,
      '_blank',
      'noopener noreferrer',
    );
  };

  const renderLogo = () => {
    if (isYesMerchant()) {
      return (
        <div className="flex">
          <Image src={LOGO_MAPPING.MERCHANT} size="small" />
          <div className="divider" />
          <Image src={LOGO_MAPPING.YESMERCHANT} className="yes-logo" />
        </div>
      );
    }
    if (isICICIDashboard()) {
      return <Image src={LOGO_MAPPING.ICICIMERCHANT} className="icici-logo" />;
    }

    if (isIDFCDashboard()) {
      return <Image src={LOGO_MAPPING.IDFCMERCHANT} className="icici-logo" />;
    }
    if (isLKDashboard()) {
      return (
        <div className="flex">
          <Image src={LOGO_MAPPING.MERCHANT} size="small" />
          <div className="divider" />
          <Image src={LOGO_MAPPING.LENDINGKARTMERCHANT} className="lk-logo" />
        </div>
      );
    }
    if (isAxisDashboard()) return <Image src={LOGO_MAPPING.AXISEASYPAYPLUS} />;
    return <Image src={LOGO_MAPPING.MERCHANT} size="small" />;
  };

  const renderHeader = () => (
    <Space
      justifyContent="space-between"
      alignItems="center"
      className="left-container-header w100"
    >
      <div className="logo-wrapper">{renderLogo()}</div>
      <Conditional if={onHomePage}>
        <div className="w-600 user-select-none">
          <span className="text-light">
            {formName === SIGNUP
              ? 'Have an account?'
              : "Don't have an account?"}
          </span>{' '}
          <Button link className="header-btn p-0" onClick={switchForm}>
            {formName === SIGNUP ? 'Log In' : 'Sign Up'}
          </Button>
        </div>
      </Conditional>
    </Space>
  );

  const renderTncText = () => (
    <Conditional if={showSignupFooter}>
      <Text variant="b12" color="bodyLight" className="mr-top-16">
        By signing up, you accept the Cashfree{' '}
        <span className="text-primary pointer" onClick={openTncLink}>
          Terms & Conditions
        </span>
      </Text>
    </Conditional>
  );

  const renderRecaptchaText = () => (
    <Conditional if={showRecaptchaText}>
      {/* Mandatory for reCAPTCHA v3 - Do not remove */}
      <div className="mr-top-16 text-center recaptcha-text">
        Protected by reCAPTCHA. Google{' '}
        <div>
          <a
            href={GOOGLE_PRIVACY_POLICY}
            target="_blank"
            className="text-primary"
            rel="noopener noreferrer"
            onClick={() => {
              Analytics.trackEvent(
                EVENTS.GOOGLE_LOGIN.PRIVACY_POLICY,
                null,
                'OB',
              )();
            }}
          >
            Privacy Policy
          </a>{' '}
          &amp;{' '}
          <a
            href={GOOGLE_TERMS_OF_SERVICE}
            target="_blank"
            className="text-primary"
            rel="noopener noreferrer"
            onClick={() => {
              Analytics.trackEvent(
                EVENTS.GOOGLE_LOGIN.TERMS_OF_SERVICE,
                null,
                'OB',
              )();
            }}
          >
            Terms of Service
          </a>{' '}
          apply.
        </div>
      </div>
    </Conditional>
  );

  const handle2faAuthConfirm = values =>
    new Promise(resolve => {
      verifyLoginOTP({
        body: {
          id: active2faMethodData.id,
          otp: values,
        },
        resolve,
        cb: (data, token) => {
          userObj.login(data);
          navigateAsPerUserFlow(token);
          Analytics.trackEvent(
            EVENTS[EMAIL_LOGIN].LOGIN,
            { loginType: 'Email ID', Response: 'Success', '2FA_OTP': true },
            'OB',
          )();
        },
        errorCb: () => {
          Analytics.trackEvent(
            EVENTS[EMAIL_LOGIN].LOGIN,
            { loginType: 'Email ID', Response: 'Failure', '2FA_OTP': true },
            'OB',
          )();
        },
      });
    });

  const handle2faCancel = () => {
    toggleOTPModal(false);
  };
  const handle2faResendOTP = () => {
    resendLoginOTP({
      data: active2faMethodData.id,
    });
  };

  const renderFooter = () => (
    <>
      <Conditional if={showSignupFooter}>
        <Space alignItems="center" className="contact-us">
          <Space direction="column" alignItems="center">
            <Conditional if={signUpFormStatus === USER_DETAILS_COMPLETED}>
              <Text className="mr-btm-8">Facing issues?</Text>
              <Button
                secondary
                size="small"
                onClick={() =>
                  window.open(CF_SUPPORT_URL, '_blank', 'noopener noreferrer')
                }
              >
                Need help?
              </Button>
            </Conditional>
            <Conditional if={signUpFormStatus !== USER_DETAILS_COMPLETED}>
              <Text className="mr-btm-8">Facing issue with a transaction?</Text>
              <Button secondary size="small" onClick={openContactSupport}>
                Contact Us
              </Button>
            </Conditional>
          </Space>
        </Space>
      </Conditional>
      <Conditional if={formName === LOGIN}>
        <Space
          justifyContent="center"
          alignItems="flex-end"
          className="gplay-wrapper"
        >
          <Space alignItems="center">
            <span className="w-600 text-light mr-right-8">
              Check out Cashfree Payments App on
            </span>
            <span className="pointer" onClick={openPlayStoreAppLink}>
              <CustomIcon componentName="G_PLAY" />
            </span>
            <span className="pointer mr-left-7" onClick={openAppleAppLink}>
              <CustomIcon componentName="A_APP" />
            </span>
          </Space>
        </Space>
      </Conditional>
    </>
  );

  const renderOnboardingContainer = () => (
    <Space
      direction="column"
      justifyContent="space-between"
      alignItems="center"
      className={`left-container ${
        formName === FORGOT_PASSWORD || formName === RESET_PASSWORD
          ? 'w100'
          : ''
      }`}
    >
      <Space direction="column" alignItems="center" className="w100">
        {renderHeader()}
        <Space
          direction="column"
          alignItems="center"
          className="left-container-content"
        >
          <FormController
            componentName={formName.toUpperCase()}
            referrer={referrer}
            formName={formName}
            signUpFormStatus={signUpFormStatus}
            loginForm={loginForm}
            userDetails={userDetails}
            signupFirstStepData={signupFirstStepData}
            merchantDetails={merchantDetails}
            emailLogin={emailLogin}
            aliasLogin={aliasLogin}
            switchForm={switchForm}
            isFormSubmitted={isFormSubmitted}
            navigateAsPerUserFlow={navigateAsPerUserFlow}
            otpModal={otp2faModal}
            toggleOTPModal={toggleOTPModal}
            storeErrorLoginResponse={storeErrorLoginResponse}
          />
          <Conditional
            if={onHomePage && !(referrerFromQueryString === PARTNER && refCode)}
          >
            <GoogleLogin
              enableLoader={enableLoader}
              disableLoader={disableLoader}
              formName={formName}
              signUpFormStatus={signUpFormStatus}
              loginForm={loginForm}
              googleLogin={googleLogin}
              checkEmailExists={checkEmailExists}
              navigateAsPerUserFlow={navigateAsPerUserFlow}
              resetModalName={resetModalName}
              setAccountExistModalData={setAccountExistModalData}
              onboardingActions={{ toggleOTPModal, storeErrorLoginResponse }}
            />
          </Conditional>
          {renderTncText()}
          {renderRecaptchaText()}
        </Space>
      </Space>
      {renderFooter()}
    </Space>
  );

  return (
    <>
      <CashfreeGlobalStyle />
      <Loader active={isLoading} />
      <HeaderScriptLoader
        includeHotjarScript={includeScripts}
        includeCriteoSignupScript={includeScripts}
      />
      <Space className="onboarding-container">
        {renderOnboardingContainer()}
        <Conditional
          if={formName !== FORGOT_PASSWORD && formName !== RESET_PASSWORD}
        >
          <div className="right-container">
            {formName === SIGNUP ? (
              <SignupBanner />
            ) : (
              <LoginBanner formName={formName} />
            )}
          </div>
        </Conditional>
        <Conditional if={!!modalName && !otp2faModal}>
          <OnboardingModal
            modalName={modalName}
            closeModal={closeModal}
            googleLogin={googleLogin}
            navigateAsPerUserFlow={navigateAsPerUserFlow}
            enableLoader={enableLoader}
            formName={formName}
            modalData={accountExistModalData}
            onboardingActions={{ toggleOTPModal, storeErrorLoginResponse }}
            setSignupFirstStepData={setSignupFirstStepData}
            updateFormStatus={updateFormStatus}
          />
        </Conditional>
      </Space>
      <CashfreeGlobalStyle />
      {otp2faModal && (
        <TwoFactorAuthModal
          open={otp2faModal}
          type={active2faMethodData.authType}
          infoLabel={getInfoText(active2faMethodData)}
          authInfo={{
            timerInSeconds: 10,
          }}
          onConfirm={handle2faAuthConfirm}
          onClose={handle2faCancel}
          onResend={handle2faResendOTP}
        />
      )}
    </>
  );
}

OnboardingContainer.propTypes = {
  enableLoader: PropTypes.func.isRequired,
  disableLoader: PropTypes.func.isRequired,
  onboardingContainer: PropTypes.object.isRequired,
  googleLogin: PropTypes.func.isRequired,
  resetPasswordData: PropTypes.object.isRequired,
  resetData: PropTypes.func.isRequired,
  toggleOTPModal: PropTypes.func.isRequired,
  storeErrorLoginResponse: PropTypes.func.isRequired,
  verifyLoginOTP: PropTypes.func.isRequired,
  resendLoginOTP: PropTypes.func.isRequired,
  checkEmailExists: PropTypes.func.isRequired,
  setSignupFirstStepData: PropTypes.func.isRequired,
  updateFormStatus: PropTypes.func.isRequired,
};

const mapStateToProps = createStructuredSelector({
  onboardingContainer: makeSelectOnboardingContainer(),
  resetPasswordData: makeSelectResetPassword(),
});

function mapDispatchToProps(dispatch) {
  return {
    enableLoader: () => dispatch(enableLoaderAction()),
    disableLoader: () => dispatch(disableLoaderAction()),
    googleLogin: data => dispatch(googleLoginAction(data)),
    resetData: () => dispatch(resetDataAction()),
    toggleOTPModal: data => dispatch(toggleOTPModalAction(data)),
    storeErrorLoginResponse: data =>
      dispatch(storeErrorLoginResponseAction(data)),
    verifyLoginOTP: data => dispatch(verifyLoginOTPAction(data)),
    resendLoginOTP: data => dispatch(resendLoginOTPAction(data)),
    checkEmailExists: data => dispatch(checkEmailExistsAction(data)),
    setSignupFirstStepData: data =>
      dispatch(setSignupFirstStepDataAction(data)),
    updateFormStatus: data => dispatch(updateFormStatusAction(data)),
  };
}

const withConnect = connect(
  mapStateToProps,
  mapDispatchToProps,
);

export default compose(
  withConnect,
  memo,
)(OnboardingContainer);
